/**
 * WellyTails Pet Food Calculator
 *
 * This script powers the pet food calculator based on client requirements.
 * It is designed to be self-contained and integrated into a Shopify page.
 *
 * HTML Dependencies:
 * This script expects to find HTML elements with specific IDs for all inputs and outputs.
 * Example: <select id="recipe-select"></select>, <input type="number" id="dog-weight">, etc.
 */

document.addEventListener('DOMContentLoaded', async () => {

    // --- 1. DATA AND CONSTANTS ---

    /**
     * Recipe data loaded from recipes.json file.
     * Each object contains the necessary values for calculation based on a 1000g formula.
     */
    let recipeData = {};

    // Load recipe data from JSON file
    try {
        const response = await fetch(window.wellytails_calc_assets.recipes_url);
        if (!response.ok) {
            throw new Error('Failed to load recipes.json');
        }
        const jsonData = await response.json();
        
        // Transform the JSON data to match the expected format
        recipeData = {};
        for (const [key, recipe] of Object.entries(jsonData)) {
            recipeData[key] = {
                displayName: recipe.displayName,
                meatGrams: recipe.meatGrams,
                baseMixGrams: recipe.baseMixGrams,
                waterGrams: recipe.waterGrams,
                meKcalKg: recipe.meKcalKg,
                // Store the full recipe data for print feature
                fullRecipe: recipe
            };
        }
    } catch (error) {
        console.error('Error loading recipes.json:', error);
        // Fallback to empty object - the app will show an error
        recipeData = {};
    }

    /**
     * Activity multipliers for dogs.
     * IMPORTANT: These are common industry standards and MUST be confirmed with the client.
     */
    const activityMultipliers = {
        'puppy_small': 3.0,
        'puppy_large': 2.0,
        'adult': 1.6,
        'active': 2.0,
        'inactive': 1.4,
    };

    const LBS_TO_KG = 0.453592;
    const G_TO_LBS = 0.00220462;

    // --- 2. DOM ELEMENT REFERENCES ---

    // Inputs
    const recipeSelect = document.getElementById('recipe-select');
    const dogNameInput = document.getElementById('dog-name');
    const dogWeightInput = document.getElementById('dog-weight');
    const weightUnitSelect = document.getElementById('weight-unit');
    const lifestyleSelect = document.getElementById('lifestyle-select');
    const calorieOverrideInput = document.getElementById('calorie-override');
    const modeSelect = document.getElementsByName('calculation-mode');

    // Mode-specific input wrappers
    const daysModeInputs = document.getElementById('days-mode-inputs');
    const meatModeInputs = document.getElementById('meat-mode-inputs');

    // Inputs for Mode A (by days)
    const numDaysInput = document.getElementById('num-days');
    const meatCostInput = document.getElementById('meat-cost');

    // Inputs for Mode B (by meat amount)
    const meatAmountInput = document.getElementById('meat-amount');

    // Outputs
    const resultsContainer = document.getElementById('results-container');
    const estimatedCaloriesSpan = document.getElementById('estimated-calories');
    const resultsHtml = document.getElementById('results-html');


    // --- 3. CORE FUNCTIONS ---

    /**
     * Calculates Resting Energy Requirement (RER)
     * @param {number} weightKg - Dog's weight in kilograms.
     * @returns {number} RER in kcal/day.
     */
    const calculateRer = (weightKg) => 70 * Math.pow(weightKg, 0.75);

    /**
     * Calculates Maintenance Energy Requirement (MER)
     * @param {number} rer - The dog's RER.
     * @param {string} lifestyle - The key for the activity multiplier.
     * @returns {number} MER in kcal/day.
     */
    const calculateMer = (rer, lifestyle) => rer * (activityMultipliers[lifestyle] || 1.6);


    /**
     * Main function to perform all calculations and display results.
     * This function is called whenever an input value changes.
     */
    const calculateAndDisplayResults = () => {
        // Requirement to calculate based on dog details. [cite: 13, 335, 336]
        const weight = parseFloat(dogWeightInput.value);
        const lifestyle = lifestyleSelect.value;
        const recipeKey = recipeSelect.value;
        const selectedRecipe = recipeData[recipeKey];

        // --- Input Validation ---
        if (!weight || weight <= 0 || !lifestyle || !selectedRecipe) {
            resultsContainer.style.display = 'none';
            return;
        }

        const weightKg = (weightUnitSelect.value === 'lbs') ? weight * LBS_TO_KG : weight;
        
        // --- Determine Target Daily Calories ---
        // This implements the hybrid model: calculate MER but allow user override.
        const rer = calculateRer(weightKg);
        const estimatedMer = calculateMer(rer, lifestyle);
        estimatedCaloriesSpan.textContent = Math.round(estimatedMer);

        const calorieOverride = parseFloat(calorieOverrideInput.value);
        const targetDailyCalories = (calorieOverride && calorieOverride > 0) ? calorieOverride : estimatedMer;

        // --- Perform Calculations based on selected mode ---
        const selectedMode = document.querySelector('input[name="calculation-mode"]:checked').value;
        let output = {};

        if (selectedMode === 'days') {
            // Requirement for "Flow 1": Calculate based on number of days. [cite: 16, 342]
            const numDays = parseInt(numDaysInput.value) || 1;
            const meatCost = parseFloat(meatCostInput.value) || 0;

            // Calculations for Flow 1
            const dailyFoodG = (targetDailyCalories / selectedRecipe.meKcalKg) * 1000;
            const totalFoodG = dailyFoodG * numDays;
            const totalMeatG = totalFoodG * (selectedRecipe.meatGrams / 1000);
            const totalBaseMixG = totalFoodG * (selectedRecipe.baseMixGrams / 1000);
            const totalWaterG = totalFoodG * (selectedRecipe.waterGrams / 1000);
            const totalMeatLbs = totalMeatG * G_TO_LBS;
            const finalCost = totalMeatLbs * meatCost; // Requirement for cost estimation 

            output = {
                dogName: dogNameInput.value || 'Your dog',
                duration: `${numDays} day(s)`,
                totalMeat: totalMeatG,
                totalBaseMix: totalBaseMixG,
                totalWater: totalWaterG,
                cost: finalCost.toFixed(2),
                dailyFood: dailyFoodG
            };

        } else { // mode === 'meat'
            // Requirement for "Flow 2": Calculate based on meat amount. 
            const meatAmount = parseFloat(meatAmountInput.value) || 0;
            if (meatAmount <= 0) {
                 resultsContainer.style.display = 'none';
                 return;
            }

            // Calculations for Flow 2
            const dailyFoodG = (targetDailyCalories / selectedRecipe.meKcalKg) * 1000;
            const dailyMeatG = dailyFoodG * (selectedRecipe.meatGrams / 1000);
            
            // Assume meat amount is in lbs, matching common user input
            const userMeatG = meatAmount * (1 / G_TO_LBS);
            const durationInDays = userMeatG / dailyMeatG;
            const totalFoodG = userMeatG / (selectedRecipe.meatGrams / 1000);
            const totalBaseMixG = totalFoodG * (selectedRecipe.baseMixGrams / 1000);
            const totalWaterG = totalFoodG * (selectedRecipe.waterGrams / 1000);

            output = {
                dogName: dogNameInput.value || 'Your dog',
                duration: `${durationInDays.toFixed(1)} day(s)`,
                totalMeat: userMeatG,
                totalBaseMix: totalBaseMixG,
                totalWater: totalWaterG,
                cost: null, // Cost is not calculated in this mode
                dailyFood: dailyFoodG
            };
        }
        
        displayResults(output);
    };
    
    /**
     * Renders the calculated results into the results container.
     * @param {object} results - The object containing all calculated values.
     */
    const displayResults = (results) => {
    let html = `
        <h3>Results for ${results.dogName}</h3>
        <p><strong>Daily Food Requirement:</strong> ${Math.round(results.dailyFood)}g of prepared food.</p>
        <p>For a period of <strong>${results.duration}</strong>, you will need to prepare the following:</p>
        <ul>
            <li><strong>Meat:</strong> ${Math.round(results.totalMeat)}g (${(results.totalMeat * G_TO_LBS).toFixed(2)} lbs)</li>
            <li><strong>WellyChef Base Mix:</strong> ${Math.round(results.totalBaseMix)}g</li>
            <li><strong>Water:</strong> ${Math.round(results.totalWater)}g (or ml)</li>
        </ul>
    `;
    if (results.cost !== null) {
        html += `<p class="cost-result"><strong>Estimated Meat Cost:</strong> $${results.cost}</p>`;
    }
    
    // Action buttons for Print and Email
    html += `
        <div class="action-buttons-container">
            <button class="print-button" id="print-btn">📄 Download PDF</button>
            <button class="email-button" id="email-btn">📧 Email PDF</button>
        </div>
    `;

    resultsHtml.innerHTML = html;
    resultsContainer.style.display = 'block';
};


    // --- 4. EVENT LISTENERS AND INITIALIZATION ---

    /**
     * Populates the recipe select dropdown from the recipeData object.
     */
    const populateRecipes = () => {
        // Requirement to allow recipe selection. 
        for (const key in recipeData) {
            const option = document.createElement('option');
            option.value = key;
            option.textContent = recipeData[key].displayName;
            recipeSelect.appendChild(option);
        }
    };
    
    /**
     * Toggles the visibility of mode-specific inputs.
     */
    const handleModeChange = () => {
        const selectedMode = document.querySelector('input[name="calculation-mode"]:checked').value;
        if (selectedMode === 'days') {
            daysModeInputs.style.display = 'block';
            meatModeInputs.style.display = 'none';
        } else {
            daysModeInputs.style.display = 'none';
            meatModeInputs.style.display = 'block';
        }
        calculateAndDisplayResults(); // Recalculate when mode changes
    };

    // Attach all event listeners
    const inputsToWatch = [
        recipeSelect, dogWeightInput, weightUnitSelect, lifestyleSelect, 
        calorieOverrideInput, numDaysInput, meatCostInput, meatAmountInput
    ];
    inputsToWatch.forEach(input => input.addEventListener('input', calculateAndDisplayResults));
    modeSelect.forEach(radio => radio.addEventListener('change', handleModeChange));
    
    // --- Initial Setup ---
    populateRecipes();
    handleModeChange(); // Set initial visibility
    calculateAndDisplayResults(); // Run initial calculation
});